/** @flow */
import React from 'react';
import { NavLink, withRouter } from 'react-router-dom';
import { translate } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import styled, { withTheme } from 'styled-components';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListSubheader from '@material-ui/core/ListSubheader';
import FeaturePermission from 'common/components/FeaturePermission';
import BetaTitle from 'statsradio/components/BetaTitle';
import DevFlag from 'common/components/DevFlag';

import * as Routes from 'statsradio/helpers/constant/Routes';
import RadioConfiguredComponent from '../../components/RadioConfiguredComponent';
import CurrentRadioHeader from './CurrentRadioHeader';
import Permissions from '../../modules/authorisation/Permissions';
import AuthorisedComponent from '../../modules/authorisation/AuthorisedComponent';

const Link = styled(NavLink)`
  text-decoration: none;
  color: ${props => props.theme.V3.colors.text.primary};
  &:hover {
    color: ${props => props.theme.V3.colors.primary.normal};
  }
`;

const SIcon = styled.i`
  margin-right: 17px;
`;

type Props = {
  onLinkClick: () => void,
  t: any,
  theme: any,
};

function Menu({ onLinkClick, t, theme }: Props) {
  return (
    <div>
      <CurrentRadioHeader />
      <List>
        <RadioConfiguredComponent>
          <AuthorisedComponent permissions={[
            Permissions.ACCESS_DASHBOARD,
            Permissions.ACCESS_REACH,
            Permissions.ACCESS_LISTENING_TIME,
            Permissions.ACCESS_DEMOGRAPHY,
            Permissions.ACCESS_GEOLOCALISATION]}
          >
            <AuthorisedComponent permissions={Permissions.ACCESS_DASHBOARD}>
              <Link to={Routes.DASHBOARD} onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">dashboard</SIcon>
                  {t('menu.dashboard')}
                </ListItem>
              </Link>
            </AuthorisedComponent>
            <AuthorisedComponent permissions={Permissions.ACCESS_HISTORY}>
              <Link to="/history" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">history</SIcon>
                  <BetaTitle text={t('menu.history')} />
                </ListItem>
              </Link>
            </AuthorisedComponent>
            <AuthorisedComponent permissions={Permissions.ACCESS_REACH}>
              <Link to="/reach" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">trending_up</SIcon>
                  {t('menu.reach')}
                </ListItem>
              </Link>
            </AuthorisedComponent>
            <AuthorisedComponent permissions={Permissions.ACCESS_LISTENING_TIME}>
              <Link to="/listening-time" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">av_timer</SIcon>
                  {t('menu.listeningTime')}
                </ListItem>
              </Link>
            </AuthorisedComponent>
            <FeaturePermission feature={'DEMOGRAPHY_MODULE'}>
              <AuthorisedComponent permissions={Permissions.ACCESS_DEMOGRAPHY}>
                <Link to="/demography" onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">pie_chart</SIcon>
                    <BetaTitle text={t('menu.demography')} />
                  </ListItem>
                </Link>
              </AuthorisedComponent>
            </FeaturePermission>
          </AuthorisedComponent>

          <FeaturePermission feature={'QUALITATIVE_MODULE'}>
            <AuthorisedComponent permissions={Permissions.ACCESS_QUALITATIVE}>
              <Link to="/survey" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">pie_chart</SIcon>
                  <BetaTitle text={t('menu.survey')} />
                </ListItem>
              </Link>
            </AuthorisedComponent>
          </FeaturePermission>

          <DevFlag flag="STREAMING_METRICS">
            <AuthorisedComponent permissions={Permissions.ACCESS_STREAMING}>
              <ListSubheader>{t('menu.technical')}</ListSubheader>
              <Link to={Routes.STREAMING} onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">pie_chart</SIcon>
                  <BetaTitle text={t('menu.streams')} />
                </ListItem>
              </Link>
            </AuthorisedComponent>
          </DevFlag>

          <AuthorisedComponent permissions={[
            Permissions.ACCESS_SALES,
            Permissions.ACCESS_SPOTS,
            Permissions.ACCESS_CAMPAIGNS,
            Permissions.MANAGE_CUSTOMERS,
            Permissions.MANAGE_CAMPAIGNS_ADVERTISEMENT]}
          >
            <ListSubheader>{t('menu.salesSection')}</ListSubheader>

            <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
              <FeaturePermission feature="FOLLO_MODULE">
                <Link to="/sales-dashboard" onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">dashboard</SIcon>
                    {t('menu.dashboard')}
                  </ListItem>
                </Link>
              </FeaturePermission>

              <Link to="/sales-flyer" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">insert_drive_file</SIcon>
                  {t('menu.summary')}
                </ListItem>
              </Link>

              <Link to="/estimations" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">monetization_on</SIcon>
                  {t('menu.estimations')}
                </ListItem>
              </Link>
            </AuthorisedComponent>

            <FeaturePermission feature="FOLLO_MODULE">
              <AuthorisedComponent permissions={[Permissions.ACCESS_SALES]}>
                <Link to="/propositions" onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">view_list</SIcon>
                    <BetaTitle text={t('menu.propositions')} />
                  </ListItem>
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={[Permissions.ACCESS_CAMPAIGNS]}>
                <Link to={Routes.CAMPAIGNS} onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">assignment</SIcon>
                    <BetaTitle text={t('menu.campaigns')} />
                  </ListItem>
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.ACCESS_SPOTS}>
                <Link to="/spots" onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">access_time</SIcon>
                    <BetaTitle text={t('menu.spots')} />
                  </ListItem>
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.MANAGE_CUSTOMERS}>
                <Link to="/customers" onClick={onLinkClick}>
                  <ListItem>
                    <SIcon className="material-icons">account_circle</SIcon>
                    {t('menu.customers')}
                  </ListItem>
                </Link>
              </AuthorisedComponent>

              <AuthorisedComponent permissions={Permissions.ACCESS_SALES}>
                <FeaturePermission feature="SALES_INVENTORY_MODULE">
                  <Link to="/spot-inventory" onClick={onLinkClick}>
                    <ListItem>
                      <SIcon className="material-icons">view_module</SIcon>
                      {t('menu.inventory')}
                    </ListItem>
                  </Link>

                  <DevFlag flag="SALES_PROMOTIONS">
                    <Link to="/promotions" onClick={onLinkClick}>
                      <ListItem>
                        <SIcon className="material-icons">whatshot</SIcon>
                        {t('menu.promotions')}
                      </ListItem>
                    </Link>
                  </DevFlag>

                </FeaturePermission>
              </AuthorisedComponent>

            </FeaturePermission>
          </AuthorisedComponent>
        </RadioConfiguredComponent>

        <AuthorisedComponent permissions={[Permissions.BILL_RADIOS, Permissions.CREATE_DEMO_CAMPAIGN]}>
          <ListSubheader>{t('menu.statsRadioAdministration')}</ListSubheader>

          <FeaturePermission feature="FOLLO_MODULE">
            <AuthorisedComponent permissions={Permissions.CREATE_DEMO_CAMPAIGN}>
              <Link to="/create-demo-campaign" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">assignment</SIcon>
                  {t('menu.demoCampaign')}
                </ListItem>
              </Link>
            </AuthorisedComponent>
          </FeaturePermission>

        </AuthorisedComponent>

        <DevFlag flag="RADIO_CONFIGURATION">
          <RadioConfiguredComponent allowOnlyNotConfigured>
            <AuthorisedComponent permissions={Permissions.ACCESS_CONFIGURATION}>
              <ListSubheader>{t('menu.configuration')}</ListSubheader>
              <Link to="/configure-radio" onClick={onLinkClick}>
                <ListItem>
                  <SIcon className="material-icons">settings</SIcon>
                  {t('menu.radioConfiguration')}
                </ListItem>
              </Link>
            </AuthorisedComponent>
          </RadioConfiguredComponent>
        </DevFlag>
      </List>
      <ReactTooltip />
    </div>
  );
}

export default withRouter(translate()(withTheme(Menu)));



// WEBPACK FOOTER //
// ./src/statsradio/layout/v2/Menu.js